import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
 Search, Eye, Tag, Plus, FileText, 
   X, User, Package, Download 
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

const OrderStatusBadge = ({ status }) => {
  const styles = {
    completed: "bg-green-100 text-green-700 border-green-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    failed: "bg-red-100 text-red-700 border-red-200",
    refunded: "bg-slate-100 text-slate-700 border-slate-200"
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

const handleDownloadInvoice = async (orderId, invoiceNumber) => {
    try {
      // 1. Request with explicit responseType 'blob'
      const response = await axiosClient.get(`/admin/orders/${orderId}/invoice`, {
        responseType: 'blob',
      });

      // 2. SAFETY CHECK: Did the interceptor return 'response' or 'response.data'?
      // If 'response' is a Blob directly, use it. 
      // If 'response' is an object with a 'data' property that is a Blob, use that.
      const blobData = (response instanceof Blob) ? response : response.data;

      // 3. Create the Blob explicitly with PDF type
      const file = new Blob([blobData], { type: 'application/pdf' });

      // 4. Create Link & Click
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${invoiceNumber || orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // 5. Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Download Error:", error);
      alert("Failed to download invoice. Check console for details.");
    }
  };
  
// Helper to get symbol
const getSymbol = (currencyCode) => {
    return currencyCode === 'INR' ? '₹' : '$';
};

const OrdersPage = () => {
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null); 
  const [showCreateModal, setShowCreateModal] = useState(false); 
  const [couponCode, setCouponCode] = useState(''); 
  const [couponError, setCouponError] = useState('');

  const [createData, setCreateData] = useState({
    user_id: '', product_id: '', payment_method: 'online_pending', coupon_code: '',
    currency: 'USD' 
  });

  // Queries
  const { data } = useQuery({ 
    queryKey: ['orders', search, statusFilter], 
    queryFn: () => axiosClient.get('/admin/orders', { params: { search, status: statusFilter } }),
    keepPreviousData: true
  });
  const { orders = [], total = 0 } = data || {};

  const { data: orderDetail } = useQuery({
    queryKey: ['orderDetail', selectedOrder],
    queryFn: () => axiosClient.get(`/admin/orders/${selectedOrder}`),
    enabled: !!selectedOrder
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers_list'],
    queryFn: () => axiosClient.get('/admin/customers', { params: { limit: 100 } }),
    enabled: showCreateModal
  });
  const customers = customersData?.customers || [];

  const { data: packages = [] } = useQuery({
    queryKey: ['packages_list'],
    queryFn: () => axiosClient.get('/admin/packages'),
    enabled: showCreateModal
  });

  // Mutations
  const createOrderMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/orders', {
      ...data, 
      user_id: parseInt(data.user_id),
      product_id: parseInt(data.product_id)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      setShowCreateModal(false);
      setCreateData({ user_id: '', product_id: '', payment_method: 'online_pending', coupon_code: '', currency: 'USD' });
      setCouponError(''); // Clear error
      alert("Order Created Successfully!");
    },
    onError: (err) => {
        // ✅ Check if error is related to Coupon
        if (err.message.includes("Coupon")) {
            setCouponError(err.message);
        } else {
            alert(`Failed: ${err.message}`);
        }
    }
  });

  const updateCouponMutation = useMutation({
    mutationFn: ({ id, code }) => axiosClient.put(`/admin/orders/${id}/coupon`, { coupon_code: code }),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['orderDetail']);
      alert("Coupon Applied! Invoice updated.");
    },
    onError: () => alert("Failed.")
  });

  const getPackagePrice = (pkg) => {
      const priceObj = pkg.pricing?.find(p => p.currency === createData.currency);
      return priceObj ? priceObj.amount : 0;
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Order Management</h1>
          <p className="text-sm text-slate-500">Total {total} Records</p>
        </div>
        
        <div className="flex space-x-2 w-full md:w-auto">
          <select className="p-2 border rounded-lg text-sm bg-white" onChange={e => setStatusFilter(e.target.value)}>
             <option value="">All Status</option>
             <option value="completed">Completed</option>
             <option value="pending">Pending</option>
          </select>
          <div className="relative flex-1">
            <input 
              className="pl-9 pr-4 py-2 border rounded-lg text-sm w-full md:w-64 focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Search Invoice/Email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          </div>
          {can('orders:create') && (
            <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 shadow-lg font-bold text-sm">
                <Plus className="w-4 h-4 mr-2" /> Manual Order
            </button>
          )}
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Invoice #</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Customer</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Product</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Total</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Status</th>
              <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase">Date</th>
              <th className="px-6 py-4 text-right">View</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {orders.length === 0 ? <tr><td colSpan="7" className="p-8 text-center text-slate-400">No orders found.</td></tr> : orders.map((o) => (
              <tr key={o.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-4 font-mono text-sm font-medium text-slate-700">{o.invoice_number || '---'}</td>
                <td className="px-6 py-4">
                   <p className="text-sm font-semibold text-slate-800">{o.full_name}</p>
                   <p className="text-xs text-slate-500">{o.email}</p>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">{o.product_name}</td>
                {/* ✅ DYNAMIC CURRENCY SYMBOL IN LIST */}
                <td className="px-6 py-4 font-bold text-slate-800">{getSymbol(o.currency)}{o.total_amount}</td>
                <td className="px-6 py-4"><OrderStatusBadge status={o.status} /></td>
                <td className="px-6 py-4 text-sm text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleDateString() : '-'}</td>
                <td className="px-6 py-4 text-right">
                   <button onClick={() => setSelectedOrder(o.id)} className="p-2 text-slate-400 hover:text-blue-600 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors">
                      <Eye className="w-4 h-4" />
                   </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- CREATE MODAL --- */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 backdrop-blur-sm p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <h3 className="font-bold text-lg text-slate-800">Create New Order</h3>
                 <button onClick={() => setShowCreateModal(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-600"/></button>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); createOrderMutation.mutate(createData); }} className="p-6 space-y-4">
                 
                 {/* Currency Selector */}
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Billing Currency</label>
                    <div className="flex space-x-3">
                        <button 
                            type="button"
                            onClick={() => setCreateData({...createData, currency: 'USD'})}
                            className={`flex-1 py-2 rounded-lg text-sm font-bold border transition-all ${createData.currency === 'USD' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                        >
                            USD ($)
                        </button>
                        <button 
                            type="button"
                            onClick={() => setCreateData({...createData, currency: 'INR'})}
                            className={`flex-1 py-2 rounded-lg text-sm font-bold border transition-all ${createData.currency === 'INR' ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                        >
                            INR (₹)
                        </button>
                    </div>
                 </div>

                 {/* Customer */}
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Customer</label>
                    <div className="relative">
                        <User className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                        <select className="w-full pl-9 p-2.5 border rounded-lg bg-white" required onChange={e => setCreateData({...createData, user_id: e.target.value})}>
                            <option value="">-- Select Customer --</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.full_name} ({c.email})</option>)}
                        </select>
                    </div>
                 </div>

                 {/* Package */}
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Package Plan</label>
                    <div className="relative">
                        <Package className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                        <select className="w-full pl-9 p-2.5 border rounded-lg bg-white" required onChange={e => setCreateData({...createData, product_id: e.target.value})}>
                            <option value="">-- Select Package --</option>
                            {packages.map(p => (
                                <option key={p.id} value={p.id}>
                                    {p.name} — {createData.currency === 'INR' ? `₹${getPackagePrice(p)}` : `$${getPackagePrice(p)}`}
                                </option>
                            ))}
                        </select>
                    </div>
                 </div>

                 {/* ✅ NEW: Apply Coupon Code (Restored) */}
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Apply Coupon (Optional)</label>
                    <div className="relative">
                        <Tag className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                        <input 
                            className={`w-full pl-9 p-2.5 border rounded-lg uppercase font-mono ${couponError ? 'border-red-500 focus:ring-red-200' : ''}`}
                            placeholder="e.g. WELCOME20"
                            value={createData.coupon_code}
                            onChange={e => {
                                setCreateData({...createData, coupon_code: e.target.value});
                                setCouponError(''); // Clear error while typing
                            }}
                        />
                    </div>
                    {/* ✅ Error Message Below Input */}
                    {couponError && (
                        <p className="text-xs text-red-500 mt-1 flex items-center animate-pulse">
                            <span className="mr-1">⚠️</span> {couponError}
                        </p>
                    )}
                 </div>

                 {/* Payment Method */}
                 <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Payment Status</label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className={`border p-3 rounded-lg flex items-center cursor-pointer ${createData.payment_method === 'cash' ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
                            <input type="radio" name="pay" className="hidden" onClick={() => setCreateData({...createData, payment_method: 'cash'})} />
                            <div><span className="block text-sm font-bold text-slate-700">Paid (Cash/Bank)</span></div>
                        </label>
                        <label className={`border p-3 rounded-lg flex items-center cursor-pointer ${createData.payment_method === 'online_pending' ? 'border-amber-500 bg-amber-50' : 'border-slate-200'}`}>
                            <input type="radio" name="pay" className="hidden" onClick={() => setCreateData({...createData, payment_method: 'online_pending'})} />
                            <div><span className="block text-sm font-bold text-slate-700">Pending</span></div>
                        </label>
                    </div>
                 </div>

                 <button type="submit" disabled={createOrderMutation.isLoading} className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 shadow-lg mt-4">
                    {createOrderMutation.isLoading ? 'Creating...' : 'Create Order'}
                 </button>
              </form>
           </div>
        </div>
      )}

      {/* Invoice Modal (Already Fixed in previous step, kept same) */}
      {selectedOrder && orderDetail && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50 backdrop-blur-sm p-4">
           <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="bg-slate-900 p-6 flex justify-between items-center text-white">
                 <div>
                  <h2 className="text-xl font-bold flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-400"/> Invoice Details
                  </h2>
                  <p className="text-slate-400 text-sm font-mono mt-1">{orderDetail.invoice_number}</p></div>
                 <div className="flex items-center gap-3">
                    {/* ✅ NEW DOWNLOAD BUTTON */}
                    <button 
                        onClick={() => handleDownloadInvoice(orderDetail.id, orderDetail.invoice_number)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors"
                    >
                        <Download className="w-4 h-4" /> PDF
                    </button>
                    <OrderStatusBadge status={orderDetail.status} />
                 </div>
              </div>
              <div className="p-8 overflow-y-auto flex-1">
                 <div className="grid grid-cols-2 gap-10 mb-8 border-b border-slate-100 pb-8">
                    <div>
                       <p className="text-xs font-bold text-slate-400 uppercase mb-2">Billed To</p>
                       <p className="font-bold text-slate-800 text-lg">{orderDetail.user_name}</p>
                       <p className="text-sm text-slate-600">{orderDetail.user_email}</p>
                       <p className="text-xs text-slate-500 mt-2 leading-relaxed">{orderDetail.user_address || 'Address not provided'}</p>
                    </div>
                    <div className="text-right">
                       <p className="text-xs font-bold text-slate-400 uppercase mb-2">Payment Info</p>
                       <p className="text-sm text-slate-600"><span className="font-bold">Date:</span> {new Date(orderDetail.created_at).toLocaleDateString()}</p>
                       <p className="text-sm text-slate-600"><span className="font-bold">Currency:</span> {orderDetail.currency}</p>
                       {orderDetail.paid_at && <p className="text-sm text-green-600 font-bold mt-2">PAID ON: {new Date(orderDetail.paid_at).toLocaleDateString()}</p>}
                    </div>
                 </div>

                 <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                    <table className="w-full text-sm text-left">
                       <thead className="bg-slate-50 border-b border-slate-200">
                          <tr><th className="p-3 font-bold text-slate-600">Product / Service</th><th className="p-3 text-right font-bold text-slate-600">Price</th></tr>
                       </thead>
                       <tbody>
                          {orderDetail.items.map((item, i) => (
                             <tr key={i} className="border-b border-slate-50 last:border-0">
                                <td className="p-3">
                                    <span className="font-medium text-slate-700">{item.product_name}</span>
                                    <span className="ml-2 text-xs text-slate-400">Qty: {item.quantity}</span>
                                </td>
                                <td className="p-3 text-right font-mono text-slate-600">{getSymbol(orderDetail.currency)}{item.price}</td>
                             </tr>
                          ))}
                       </tbody>
                    </table>
                 </div>

                 <div className="flex justify-end">
                    <div className="w-72 space-y-3 bg-slate-50 p-4 rounded-xl">
                       <div className="flex justify-between text-sm text-slate-600"><span>Subtotal:</span><span className="font-mono">{getSymbol(orderDetail.currency)}{orderDetail.subtotal}</span></div>
                       <div className="flex justify-between text-sm text-green-600"><span>Discount:</span><span className="font-mono">-{getSymbol(orderDetail.currency)}{orderDetail.discount_amount}</span></div>
                       <div className="flex justify-between text-sm text-slate-600 border-b border-slate-200 pb-2"><span>Tax ({orderDetail.tax_percent}%):</span><span className="font-mono">{getSymbol(orderDetail.currency)}{orderDetail.tax_amount}</span></div>
                       <div className="flex justify-between text-xl font-black text-slate-800 pt-1"><span>Total:</span><span>{getSymbol(orderDetail.currency)}{orderDetail.total_amount}</span></div>
                    </div>
                 </div>

                 {can('orders:edit') && orderDetail.status === 'pending' && (
                    <div className="mt-8 bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center justify-between">
                       <div>
                          <h4 className="font-bold text-amber-800 text-sm flex items-center"><Tag className="w-4 h-4 mr-2"/> Apply Discount</h4>
                          <p className="text-xs text-amber-600">Modifying this will regenerate the invoice total.</p>
                       </div>
                       <div className="flex gap-2">
                          <input 
                            className="p-2 border border-amber-200 rounded-lg text-sm bg-white uppercase font-mono w-32 focus:ring-2 focus:ring-amber-500 outline-none" 
                            placeholder="CODE"
                            value={couponCode}
                            onChange={e => setCouponCode(e.target.value)}
                          />
                          <button onClick={() => updateCouponMutation.mutate({ id: selectedOrder, code: couponCode })} className="bg-amber-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-amber-700">APPLY</button>
                       </div>
                    </div>
                 )}
              </div>
              <button onClick={() => setSelectedOrder(null)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5"/></button>
           </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;